import { Show, useContext } from 'solid-js';
import { useLocation, Link } from '@solidjs/router';
import { isServer } from 'solid-js/web';
import clsx from 'clsx';
import { ProductListCardData } from '../../product/product-types';
import { ImageComponent } from '../../image-component/image-component';
import { ProductIcons } from '../../product/product-icon-tab/product-icons';
import theme from '../../../style/theme';
import { ErrorCatcher } from '../../../tools/error-catcher';
import { AppContext } from '../../../app-context-provider/app-context-provider';
import { LockedContent } from '../../../ui-components/layouts/locked-content';
import { MyContentHandler } from '../../my-content-handler/my-content-handler';
import { CardFavouriteButton } from '../../my-content/card-favourite-button';
import { removeTrailingSlash } from '../../../tools/remove-trailing-slash';

export type ProductCardProps = {
    data: ProductListCardData;
    inGrouping?: boolean;
};

/* DS `ds-card-product` (Figma "Atos - new brand theme" 1436:12316). White surface on a
   muted 1px border, image band framed above the text. Hover matches the Coloplast
   Storybook product card: the DS default elevation shadow appears, nothing moves or
   scales. The link hit area is a sibling (see below), so the shadow keys off `group-hover:`
   on the wrapper; Tailwind v4 gates that on `@media (hover: hover)`, which is
   the same guard the DS uses. */
const CARD_SURFACE = clsx(
    'relative flex h-full w-full flex-col rounded-cvi-md border border-cvi-border-muted bg-cvi-bg-default pb-cvi-6 text-cvi-fg-default',
    'transition-shadow duration-200 group-hover:shadow-elevation-default',
);

export const ProductCard = (props: ProductCardProps) => {
    const { viewport, userState, pageRegistration, productsListingPage } = useContext(AppContext);

    const imageWidth = () => {
        if (viewport.width <= theme.breakpoints.TABLET && viewport.width > theme.breakpoints.MOBILE) {
            return 500; // Tablet
        }
        return 225; // Mobile and desktop
    };

    const isHiddenForPublic = () => Boolean(
        props?.data?.isHealthcareProfessional
            && !props?.data?.isVisibleForPublic
            && !userState.user
            && pageRegistration.url !== '',
    );

    const featuredImage = () => {
        if (!props?.data?.images || props?.data?.images.length === 0) {
            return '';
        }

        const featuredImage = props?.data?.images?.find((image: { isFeatured: boolean }) => image.isFeatured);

        if (featuredImage) {
            return featuredImage.file?.url;
        }

        return props?.data?.images?.[0]?.file?.url;
    };

    const productsPageQuery = () => {
        if (isServer) {
            return '';
        }

        if (useLocation().pathname !== productsListingPage.url) {
            return '';
        }

        return useLocation().search;
    };

    return (
        <ErrorCatcher componentName="Product card">
            <div class={clsx('group relative h-full', !props.inGrouping && 'max-tablet:my-8 max-mobile:!my-4')}>
                <Show when={props?.data?.url}>
                    {/* The hit area is a sibling of the card, not its parent: the favourite heart
                        is a button and must not be nested inside a link. Overlay at z-10, heart
                        at z-20 (same stacking as slider-card). Rendered before the surface so the
                        card is reached by keyboard before the heart button inside it. */}
                    <Link
                        href={isHiddenForPublic() ? useLocation().pathname : removeTrailingSlash(props?.data?.url)}
                        rel="noopener noreferrer"
                        state={{ productsPageQuery: productsPageQuery() }}
                        class={clsx(
                            'absolute inset-0 z-10 rounded-cvi-md no-underline',
                            isHiddenForPublic() && 'pointer-events-none',
                        )}
                    >
                        <span class="sr-only">{props?.data?.post_title}</span>
                    </Link>

                    <div class={CARD_SURFACE}>
                        {/* Image band — white, framed by the card's bottom border. The DS card
                            action (heart) sits inside the band's bottom-right corner, 6px above
                            the border line (Figma: 36px button at top 170 in a 212px band). */}
                        <div class="relative border-b border-cvi-border-muted p-cvi-6">
                            <div class="flex h-[180px] items-center justify-center overflow-hidden rounded-cvi-md">
                                <Show when={featuredImage()}>
                                    <ImageComponent
                                        src={featuredImage()}
                                        // Match the 180px band above (`100%` can't reach it through ImageComponent's wrappers);
                                        // a string keeps the fetch at `{width}xAUTO`, a number would also go into the image URL.
                                        height="180px"
                                        width={imageWidth()}
                                        altText={props?.data?.images?.[0]?.file?.alt || ''}
                                        imageText={''}
                                        aspectRatio={'original'}
                                        roundCorners={false}
                                        spacer={false}
                                        fit="contain"
                                    />
                                </Show>
                            </div>

                            <Show when={props?.data?.icon}>
                                <div class="absolute right-cvi-4 top-cvi-4 z-[2] flex items-center justify-center">
                                    {/* 4.55rem ≈ 50px at the 11px root — the badge size in Figma. */}
                                    <ProductIcons name={props.data.icon!} size={4.55} />
                                </div>
                            </Show>

                            <CardFavouriteButton
                                class="absolute bottom-cvi-3 right-cvi-6 z-20"
                                title={props?.data?.post_title || ''}
                                permalink={props?.data?.permalink}
                                type="product"
                            />
                        </div>

                        <div class="flex flex-1 flex-col gap-cvi-4 px-cvi-6 pt-cvi-6">
                            <h3 class="m-0 heading-md font-cvi-medium text-cvi-fg-default text-pretty">
                                {props?.data?.post_title}
                            </h3>
                            <Show when={props?.data?.shortDescription}>
                                <p class="m-0 body-sm font-cvi-regular text-cvi-fg-muted line-clamp-2">
                                    {props?.data?.shortDescription}
                                </p>
                            </Show>
                        </div>

                        <Show when={isHiddenForPublic()}>
                            <LockedContent isColumn={true} />
                        </Show>
                    </div>
                </Show>
                <MyContentHandler action='remove' permalink={props?.data?.permalink || ''} />
            </div>
        </ErrorCatcher>
    );
};
